import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { rangeFor, currentMonth, currentYear } from "@/lib/dateRange";

const MODES = ["all", "today", "week", "month", "year", "custom"];

export function DateRangeFilter({ onChange }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("all");
  const [month, setMonth] = useState(currentMonth());
  const [year, setYear] = useState(currentYear());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const setStartSafe = (v) => {
    if (v && end && v > end) { toast.error(t("invalidRange")); return; }
    setStart(v);
  };
  const setEndSafe = (v) => {
    if (v && start && v < start) { toast.error(t("invalidRange")); return; }
    setEnd(v);
  };

  const range = useMemo(() => {
    if (mode === "custom") return { start, end };
    return rangeFor(mode, month, year);
  }, [mode, month, year, start, end]);

  useEffect(() => { onChange?.(range); /* eslint-disable-next-line */ }, [range.start, range.end]);

  const chip = (k) => (
    <button key={k} type="button" onClick={() => setMode(k)} data-testid={`range-${k}-btn`}
      className={`px-1.5 py-2 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all
        ${mode === k ? "bg-primary text-primary-foreground shadow-inner" : "bg-card text-slate-600"}`}>
      {t("range" + k.charAt(0).toUpperCase() + k.slice(1))}
    </button>
  );

  return (
    <div className="space-y-2 w-full">
      <div className="grid grid-cols-6 gap-1.5 w-full">{MODES.map(chip)}</div>
      {mode === "month" && (
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="range-month-input"
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm" />
      )}
      {mode === "year" && (
        <input type="number" min="2000" max="2100" value={year} onChange={(e) => setYear(e.target.value)} data-testid="range-year-input"
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm" />
      )}
      {mode === "custom" && (
        <div className="grid grid-cols-2 gap-2 w-full">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card">
            <span className="text-xs text-slate-500 font-semibold shrink-0">{t("from")}</span>
            <input type="date" value={start} max={end || undefined} onChange={(e) => setStartSafe(e.target.value)} data-testid="range-start-input"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card">
            <span className="text-xs text-slate-500 font-semibold shrink-0">{t("to")}</span>
            <input type="date" value={end} min={start || undefined} onChange={(e) => setEndSafe(e.target.value)} data-testid="range-end-input"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
          </div>
        </div>
      )}
    </div>
  );
}
